import ReactGA from "react-ga4";

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const isGAEnabled = Boolean(GA_ID) && Boolean(import.meta.env.PROD);

// Consent gate. GA is NOT initialized until the user grants analytics consent,
// so nothing — not even the raw ReactGA.event calls scattered in components —
// is sent before then. Driven by setAnalyticsConsent (see AnalyticsProvider).
let consentGranted = false;
let initialized = false;

function ensureInitialized() {
	if (isGAEnabled && consentGranted && !initialized) {
		ReactGA.initialize(GA_ID as string);
		initialized = true;
	}
}

/** Bridge the user's cookie-consent choice into the analytics layer. */
export function setAnalyticsConsent(granted: boolean) {
	consentGranted = granted;
	ensureInitialized();
}

function canTrack(): boolean {
	return isGAEnabled && consentGranted;
}

export type AnalyticsEvent =
	| "page_view_natal"
	| "page_view_directed"
	| "page_view_synastry"
	| "page_view_tri_dial"
	| "compute_natal_chart"
	| "compute_directed_chart"
	| "compute_synastry_chart"
	| "compute_tri_dial_chart"
	| "interact_dial"
	| "interact_double_dial"
	| "interact_tri_dial"
	| "interact_synastry_dial"
	| "toggle_triple_pattern"
	| "open_midpoint_stories"
	| "open_triple_pattern_tab"
	| "refresh_birth_info"
	| "feature_used"
	| "search_performed"
	| "search_empty_result"
	| "search_tab_change"
	| "search_view_toggle"
	| "search_page_change"
	| "search_input_focus"
	| "search_syntax_help_view";

interface TrackEventParams {
	eventName: AnalyticsEvent;
	[key: string]: string | number | boolean | undefined;
}

export function trackEvent(params: TrackEventParams) {
	if (!canTrack()) return;

	const { eventName, ...customParams } = params;
	ReactGA.event(eventName as string, customParams);
}

export function trackPageView(page: "natal" | "directed" | "synastry" | "tri_dial") {
	trackEvent({ eventName: `page_view_${page}` as AnalyticsEvent });
}

export function trackCompute(chartType: "natal" | "directed" | "synastry" | "tri_dial") {
	trackEvent({
		eventName: `compute_${chartType}_chart` as AnalyticsEvent,
		chart_type: chartType,
	});
}

export function trackFeatureUse(featureName: string) {
	trackEvent({
		eventName: "feature_used",
		feature_name: featureName,
	});
}

export type SearchPatternType =
	| "full_formula"
	| "wildcard"
	| "midpoint_only"
	| "halfsum_pair"
	| "single_planet"
	| "text";

export function detectSearchPatternType(term: string): SearchPatternType {
	if (!term || term.trim() === "") {
		return "text";
	}

	const trimmedTerm = term.trim();

	// Check for full formula: contains "=" and "/" (e.g., "SU/MO=AR")
	if (trimmedTerm.includes("=") && trimmedTerm.includes("/")) {
		return "full_formula";
	}

	// Check for wildcard: contains "/*" or "*/" (e.g., "SU/*")
	if (/\/\*|\*\//.test(trimmedTerm)) {
		return "wildcard";
	}

	// Check for midpoint only: starts with "=" (e.g., "=AR")
	if (trimmedTerm.startsWith("=")) {
		return "midpoint_only";
	}

	// Check for halfsum pair: contains "/" but no "=" (e.g., "SU/MO")
	if (trimmedTerm.includes("/")) {
		return "halfsum_pair";
	}

	// Check for single planet: 2 uppercase letters only (e.g., "SU")
	if (/^[A-Z]{2}$/.test(trimmedTerm)) {
		return "single_planet";
	}

	// Everything else is text (e.g., "love")
	return "text";
}

export function trackSearchPerformed(params: {
	search_term: string;
	pattern_type: SearchPatternType;
	result_count: number;
	has_results: boolean;
	page: number;
	limit: number;
	total_pages: number;
	halfsum_count: number;
	midpoint_count: number;
}) {
	if (!canTrack()) return;

	trackEvent({
		eventName: "search_performed" as AnalyticsEvent,
		// PRIVACY: raw search_term is intentionally NOT sent to GA — only the
		// non-identifying pattern type and result counts.
		pattern_type: params.pattern_type,
		result_count: params.result_count,
		has_results: params.has_results,
		page: params.page,
		limit: params.limit,
		total_pages: params.total_pages,
		halfsum_count: params.halfsum_count,
		midpoint_count: params.midpoint_count,
	});
}

export function trackSearchEmptyResult(params: {
	search_term: string;
	pattern_type: SearchPatternType;
	page: number;
	limit: number;
}) {
	if (!canTrack()) return;

	trackEvent({
		eventName: "search_empty_result" as AnalyticsEvent,
		pattern_type: params.pattern_type,
		page: params.page,
		limit: params.limit,
	});
}

export function trackSearchTabChange(params: {
	tab_name: string;
	previous_tab: string;
	result_count: number;
}) {
	if (!canTrack()) return;

	trackEvent({
		eventName: "search_tab_change" as AnalyticsEvent,
		tab_name: params.tab_name,
		previous_tab: params.previous_tab,
		result_count: params.result_count,
	});
}

export function trackSearchViewToggle(params: {
	view_mode: string;
	previous_mode: string;
	result_count: number;
}) {
	if (!canTrack()) return;

	trackEvent({
		eventName: "search_view_toggle" as AnalyticsEvent,
		view_mode: params.view_mode,
		previous_mode: params.previous_mode,
		result_count: params.result_count,
	});
}

export function trackSearchPageChange(params: {
	page_number: number;
	total_pages: number;
	direction: "prev" | "next" | "jump";
	current_search_term: string;
}) {
	if (!canTrack()) return;

	trackEvent({
		eventName: "search_page_change" as AnalyticsEvent,
		page_number: params.page_number,
		total_pages: params.total_pages,
		direction: params.direction,
	});
}

export function trackSearchInputFocus(params: { search_term: string }) {
	if (!canTrack()) return;

	trackEvent({
		eventName: "search_input_focus" as AnalyticsEvent,
		search_term: params.search_term,
	});
}

export function trackSearchSyntaxHelpView() {
	if (!canTrack()) return;

	trackEvent({
		eventName: "search_syntax_help_view" as AnalyticsEvent,
	});
}
